import cn from 'classnames';
import { FunctionComponent } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';

import { getAssetPreviewUrl } from '../../../../utils/player/asset';
import { VideoPreview } from '../../components/VideoPreview/VideoPreview';
import { useConfig } from '../../hooks/ConfigProvider';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { useTracker } from '../../hooks/useTracker';
import { PlaylistItemManager } from '../../utils/PlaylistItemManager';

import { useProvider } from '../../hooks/useProvider';
import { IdleLayoutElements } from './IdleLayoutElements';

export const IdleLayout: FunctionComponent = () => {
  const player = usePlayer();
  const layoutRef = useRef<HTMLDivElement>(null!);
  const [isAdLoading, setIsAdLoading] = useState(false);
  const { asset } = useCurrentAsset();
  const { trackButtonClick } = useTracker();
  const {
    ui: { videoPreview },
  } = useConfig();
  const { assetProvider } = useProvider();
  const videoPreviewDefault =
    assetProvider === 'vgtv' || assetProvider === 'ab';
  const videoPreviewEnabled =
    videoPreview === undefined ? videoPreviewDefault : videoPreview;

  const previewUrl = getAssetPreviewUrl(asset);

  const handleAdRequest = useCallback(() => {
    setIsAdLoading(true);
  }, []);

  useJWPlayerEvent('adRequest', handleAdRequest);

  const handleClick = useCallback(() => {
    const run = async () => {
      const jw = player.getJWPlayer();
      if (!jw) {
        return;
      }

      const manager = PlaylistItemManager.fromPlaylist(jw);
      // This check is performed in case user opened the page, there was no autoplay, and the token expired
      if (manager && !manager.hasValidToken()) {
        const refreshed = await manager.refreshToken();
        if (refreshed) {
          jw.load(manager.getItem());
        }
      }

      // Tracking of the autoplay is based on a play reason, the default one ("external") would cause every playback to count as an autoplay.
      // https://github.schibsted.io/svp/web-player/blob/master/src/js/player/plugins/pulse-stats.js#L280
      jw.playToggle({ reason: 'interaction' });

      trackButtonClick('play-button');
    };

    run();
  }, [player, trackButtonClick]);

  if (!asset) {
    return null;
  }

  return (
    <div
      className={cn('vgp-idle-layout', {
        'vgp-idle-layout-not-started': !isAdLoading,
      })}
      onClick={isAdLoading ? undefined : handleClick}
      ref={layoutRef}
    >
      {videoPreviewEnabled && videoPreview && previewUrl && (
        <VideoPreview url={previewUrl} containerRef={layoutRef} />
      )}
      <IdleLayoutElements asset={asset} isLoading={isAdLoading} />
    </div>
  );
};
